/**
 * Banking-Grade Tri-Modal Login Form Component
 *
 * Supports three authentication modes:
 * 1. PASSWORD: Traditional username + password
 * 2. MFA: Passwordless username + MFA token (elevated security)
 * 3. PASSKEY: WebAuthn passkey authentication (maximum security)
 */

'use client';

import React, { useState } from 'react';
import { Form, Input, Button, Card, Tabs, Alert, Typography, Badge } from 'antd';
import { UserOutlined, LockOutlined, KeyOutlined, SafetyCertificateOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { useAuth } from '@/domains/auth/context/auth-context';
import webauthnService from '@/domains/auth/services/webauthn-service';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface LoginFormProps {
  onSuccess?: (result: any) => void;
  onError?: (error: string) => void;
  className?: string;
  onPasskeyAuth?: () => Promise<any>; // Added prop for passkey authentication
}

interface PasswordLoginForm {
  username: string;
  password: string;
}

interface MFALoginForm {
  username: string;
  mfa_token: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onError,
  className,
  onPasskeyAuth // Destructure new prop
}) => {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('password');

  const handlePasswordLogin = async (values: PasswordLoginForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await login({
        username: values.username,
        password: values.password,
        auth_mode: 'password'
      });

      if (result.success) {
        onSuccess?.(result);
      } else {
        setError(result.error || 'Password authentication failed');
        onError?.(result.error || 'Password authentication failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password authentication failed';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFALogin = async (values: MFALoginForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await login({
        username: values.username,
        mfa_token: values.mfa_token,
        password: '',
        auth_mode: 'mfa'
      });

      if (result.success) {
        onSuccess?.(result);
      } else {
        setError(result.error || 'MFA authentication failed');
        onError?.(result.error || 'MFA authentication failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'MFA authentication failed';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Call the parent component's passkey authentication handler
      // This will handle the complete WebAuthn flow
      if (onPasskeyAuth) {
        await onPasskeyAuth();
      } else {
        throw new Error('Passkey authentication not available');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Passkey authentication failed';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCredentialSuccess = (result: any) => {
    onSuccess?.(result);
  };

  const handleCredentialError = (error: string) => {
    setError(error);
    onError?.(error);
  };

  const handleFallbackToPassword = () => {
    setActiveTab('password');
    setError(null);
  };

  return (
    <Card className={className} style={{ maxWidth: '400px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <Title level={2}>🏦 Banking Authentication</Title>
        <Text type="secondary">Choose your security level</Text>
      </div>

      {/* Error Display */}
      {error && (
        <Alert
          title="Authentication Error"
          description={error}
          type="error"
          showIcon
          closable={{ onClose: () => setError(null) }}
          style={{ marginBottom: '16px' }}
        />
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <LockOutlined />
              <Badge color="blue" text="Standard" />
            </span>
          }
          key="password"
        >
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              🔒 Confidential Clearance • Basic Trading Access
            </Text>
          </div>
          <Form
            name="password-login"
            onFinish={handlePasswordLogin}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter your username' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Enter your username"
                disabled={isLoading}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={isLoading}
                style={{ width: '100%' }}
              >
                Standard Login
              </Button>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SafetyCertificateOutlined />
              <Badge color="orange" text="Elevated" />
            </span>
          }
          key="mfa"
        >
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              🛡️ Secret Clearance • Risk Management Access
            </Text>
          </div>
          <Form
            name="mfa-login"
            onFinish={handleMFALogin}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter your username' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Enter your username"
                disabled={isLoading}
              />
            </Form.Item>

            <Form.Item
              name="mfa_token"
              label="MFA Token (6 digits)"
              rules={[
                { required: true, message: 'Please enter your MFA token' },
                { pattern: /^\d{6}$/, message: 'MFA token must be 6 digits' }
              ]}
            >
              <Input
                prefix={<SafetyCertificateOutlined />}
                placeholder="000000"
                disabled={isLoading}
                maxLength={6}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={isLoading}
                style={{ width: '100%', backgroundColor: '#fa8c16' }}
              >
                Elevated Login
              </Button>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SecurityScanOutlined />
              <Badge color="red" text="Maximum" />
            </span>
          }
          key="passkey"
        >
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              🔐 Top Secret Clearance • Full System Access
            </Text>
          </div>
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <SecurityScanOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Hardware Security Key Required</Text>
            </div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '24px' }}>
              Click below to authenticate with your registered hardware security key
            </Text>
            <Button
              type="primary"
              danger
              icon={<KeyOutlined />}
              onClick={handlePasskeyLogin}
              loading={isLoading}
              disabled={isLoading}
              style={{ width: '100%' }}
            >
              Maximum Security Login
            </Button>
          </div>
        </TabPane>
      </Tabs>

      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Don't have an account? <a href="/register">Sign up</a>
        </Text>
      </div>
    </Card>
  );
};

export default LoginForm;

